import { useMutation, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Check, Circle, ExternalLink, Eye, EyeOff, Rocket, X } from "lucide-react";
import { useEffect, useState } from "react";

import { api } from "../../api/client";
import type { PromotionStatus, PromotionSummary } from "../../api/types";
import { attentionSelector, designate } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { Collapsible } from "../../components/Collapsible";
import { Dialog } from "../../components/Dialog";
import { Spinner } from "../../components/EmptyState";
import { PropRow } from "../../components/panel";
import { Motion, motion, Presence } from "../../components/presence";
import { Button, ButtonLink, Field, IconButton, inputClass, SectionLabel } from "../../components/ui";
import { lang, t } from "../../i18n/i18n";
import { plural } from "../../plural";
import { releaseLines } from "../SidebarVersion";
import {
  blockerText,
  dialogPhase,
  formatBuild,
  formatDowntime,
  formatElapsed,
  formatFingerprint,
  formatParisDay,
  isActive,
  isFinal,
  markOutcomeSeen,
  missingField,
  missingText,
  panelBlocker,
  PASSWORD_MAX,
  pollDelay,
  PROMOTION_KEY,
  type PromotionStepState,
  reasonText,
  requestErrorText,
  shownSteps,
  STEP_LABELS,
  trackRequest,
  useTrackedRequest,
} from "./promotion";
import { Waiting } from "../../components/Waiting";

/** Fenêtre de la promotion (registre F35, docs/11 §11.3) : les deux versions (et l'empreinte de la version), ce qui
 *  va changer, un seul champ, le mot de passe de promotion (décision d'Alexis du 06/10/2026), et le bouton explicite
 *  « Promouvoir en production » ; puis le suivi des six étapes jusqu'au résultat. Le mot de passe ne reste en mémoire
 *  que le temps de la demande ; tant que le service de promotion ne l'a pas vérifié, la fenêtre reste sur la saisie
 *  (« Vérification du mot de passe… ») ; faux (refus `code`), elle y revient, champ désigné, avec le nombre d'essais
 *  restants. Cinq mots de passe faux en une heure bloquent les promotions une heure : le blocage est annoncé avant
 *  toute saisie (empêchement `locked`), champ désactivé. Le champ se montre en clair à la demande (œil) et n'est pas
 *  dans un formulaire envoyé, réglé en code à usage unique : le navigateur ne propose pas de garder ce mot de passe,
 *  qui sert aussi au Terminal. Fermer la fenêtre n'arrête rien : la promotion continue sur le serveur et l'encadré de
 *  l'accueil la suit, puis en rappelle le résultat (« Détail » rouvre la fenêtre sur ce résultat, `shown`). Un mot de
 *  passe manquant ou trop court est désigné au clic sur « Promouvoir en production ». */

export function PromotionDialog({
  summary,
  shown = null,
  onClose,
}: {
  summary: PromotionSummary;
  /** Résultat à montrer à l'ouverture (« Détail » de l'encadré) ; sinon la promotion en cours, ou la saisie. */
  shown?: PromotionStatus | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  // Promotion engagée (« Suivre ») ou résultat demandé (« Détail ») : la fenêtre s'ouvre dessus ; sinon la saisie.
  const [status, setStatus] = useState<PromotionStatus | null>(
    shown ?? (isActive(summary.status?.state) ? summary.status : null),
  );
  const [stalled, setStalled] = useState(false);
  const [password, setPassword] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: PROMOTION_KEY });

  const promote = useMutation({
    mutationFn: () =>
      api.preprod.promote({
        password,
        preprod_build: summary.preprod.build,
        production_build: summary.production.build ?? "",
        manifest_sha256: summary.preprod.manifest_sha256 ?? "",
      }),
    onSuccess: (reply) => {
      // Le mot de passe quitte la mémoire de la fenêtre dès la demande déposée.
      setPassword("");
      setStalled(false);
      setStatus(reply.status);
      trackRequest(reply.status);
      refresh();
    },
    onError: (error) => {
      setPassword("");
      refresh();
      const field = requestErrorText(error).field;
      if (field) designate(attentionSelector(`promotion-${field}`));
    },
  });

  // Suivi du statut : 1 s en attente du service, 1,5 s pendant la promotion ; rien une fois finie.
  const requestId = status?.request_id ?? null;
  const delay = pollDelay(status?.state, true);
  useEffect(() => {
    if (!requestId || delay === null) return;
    let stop = false;
    const timer = window.setTimeout(async () => {
      try {
        const reply = await api.preprod.promotionStatus(requestId);
        if (stop) return;
        setStalled(reply.stalled);
        if (reply.status) setStatus(reply.status);
      } catch {
        // relevé manqué (serveur occupé, réseau) : nouvel essai au tour suivant
        if (!stop) setStatus((s) => (s ? { ...s } : s));
      }
    }, delay);
    return () => {
      stop = true;
      window.clearTimeout(timer);
    };
  }, [requestId, delay, status]);

  // Demande déposée depuis cette fenêtre : son statut suivi ici est aussi celui que montre l'encadré de l'accueil.
  useEffect(() => {
    if (status && useTrackedRequest.getState().status?.request_id === status.request_id) trackRequest(status);
  }, [status]);

  // Fin de la promotion (ou refus) : versions de l'encadré relues ; mot de passe refusé : retour à la saisie, champ
  // désigné.
  const state = status?.state;
  useEffect(() => {
    if (!isFinal(state)) return;
    refresh();
    if (state === "refused" && status?.reason === "code") designate(attentionSelector("promotion-password"));
  }, [state]);

  // Horloge du temps écoulé, pendant la promotion.
  const [now, setNow] = useState(() => Date.now());
  const running = isActive(state);
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [running]);

  const phase = dialogPhase(status, promote.isPending);
  const checking = phase === "sending" || phase === "checking";
  const editing = phase === "ready" || checking || phase === "refused";
  const blocker = panelBlocker(summary);
  const lockedUntil = summary.locked_until ?? null;
  // Refus « bloqué » alors que le blocage est déjà annoncé (encadré de l'empêchement) : dit une seule fois.
  const refusalShown = phase === "refused" && !(status?.reason === "locked" && blocker === "locked");
  const submit = () => {
    if (checking) return;
    if (blocker) {
      designate(attentionSelector("promotion-dialog-blocker"), "ring");
      return;
    }
    const missing = missingField(password);
    if (missing) {
      setHint(missingText(missing));
      designate(attentionSelector("promotion-password"), "field");
      return;
    }
    setHint(null);
    setRevealed(false);
    setStatus(null);
    promote.mutate();
  };
  const restart = () => {
    setStatus(null);
    promote.reset();
  };
  // Résultat vu dans la fenêtre : l'encadré de l'accueil ne le rappelle plus.
  const close = () => {
    if (status && isFinal(status.state) && status.state !== "failed_unrestored") markOutcomeSeen(status.request_id);
    onClose();
  };

  return (
    <Dialog
      title={t("Promotion en production")}
      subtitle={t("La version de la préproduction remplace celle de la production.")}
      width={560}
      onClose={close}
      dirty={editing && password !== ""}
      footer={
        editing ? (
          <>
            <Button variant="secondary" onClick={close}>
              {checking ? t("Fermer") : t("Annuler")}
            </Button>
            <Button
              data-promotion-submit
              disabled={checking}
              blocked={Boolean(blocker) || missingField(password) !== null}
              onBlocked={submit}
              onClick={submit}
            >
              {checking ? <Spinner decorative /> : <Rocket size={14} />}{" "}
              {checking ? <Waiting>{t("Vérification du mot de passe…")}</Waiting> : t("Promouvoir en production")}
            </Button>
          </>
        ) : (
          <>
            {(phase === "failed_restored" || phase === "aborted" || phase === "interrupted_restored") && (
              <Button variant="secondary" onClick={restart}>
                {t("Recommencer")}
              </Button>
            )}
            {phase === "succeeded" && (
              <ButtonLink variant="secondary" href={summary.production.url} target="_blank" rel="noopener noreferrer">
                <ExternalLink size={14} /> {t("Ouvrir la production")}
              </ButtonLink>
            )}
            <Button variant={phase === "succeeded" ? "primary" : "secondary"} onClick={close}>
              {t("Fermer")}
            </Button>
          </>
        )
      }
    >
      {editing ? (
        <ReadyBody
          summary={summary}
          password={password}
          disabled={checking || Boolean(blocker)}
          revealed={revealed}
          onReveal={() => setRevealed((r) => !r)}
          onPassword={(value) => {
            setPassword(value);
            setHint(null);
          }}
          onSubmit={submit}
        />
      ) : (
        status && <ProgressBody phase={phase} status={status} summary={summary} stalled={stalled} now={now} />
      )}
      <div className="flex flex-col gap-3 px-4 pb-4 empty:hidden">
        <Presence show={checking}>
          {checking && (
            <div role="status">
              {phase === "checking" && stalled ? (
                <Callout tone="warn">
                  {t("Le service de promotion ne répond pas : la demande attend d'être prise en charge. La production n'a pas changé.")}
                </Callout>
              ) : (
                <Callout tone="info">
                  {t("Vérification du mot de passe par le service de promotion… La production n'a pas changé.")}
                </Callout>
              )}
            </div>
          )}
        </Presence>
        <Presence show={editing && !checking && Boolean(blocker)}>
          {editing && !checking && blocker && (
            <div data-attention="promotion-dialog-blocker" className="rounded-lg">
              <Callout tone={blocker === "locked" ? "warn" : "info"}>{blockerText(blocker, lockedUntil)}</Callout>
            </div>
          )}
        </Presence>
        <Presence show={refusalShown}>
          {refusalShown && status && (
            <Callout tone="danger" title={t("Promotion refusée")}>
              {reasonText(status.reason, status)} {t("La production n'a pas changé.")}
            </Callout>
          )}
        </Presence>
        <Presence show={editing && promote.isError}>
          {editing && promote.isError && <Callout tone="danger">{requestErrorText(promote.error).text}</Callout>}
        </Presence>
        <Presence show={editing && Boolean(hint) && !promote.isError}>
          {editing && hint && !promote.isError && <Callout tone="info">{hint}</Callout>}
        </Presence>
      </div>
    </Dialog>
  );
}

/** Étape « Prête » : versions, ce qui va changer, mot de passe de promotion. Pas de formulaire envoyé (Entrée dans le
 *  champ vaut le bouton) et un champ réglé en code à usage unique : le navigateur ne propose ni de garder ce mot de
 *  passe, ni de le remplir de lui-même. L'œil le montre en clair le temps de le relire ; il revient masqué à l'envoi. */
export function ReadyBody({
  summary,
  password,
  disabled,
  revealed,
  onReveal,
  onPassword,
  onSubmit,
}: {
  summary: PromotionSummary;
  password: string;
  disabled: boolean;
  revealed: boolean;
  onReveal: () => void;
  onPassword: (value: string) => void;
  onSubmit: () => void;
}) {
  const reveal = revealed ? t("Masquer le mot de passe") : t("Afficher le mot de passe");
  return (
    <div className="space-y-4 px-4 py-4">
      <Versions summary={summary} />
      <ReleaseReview summary={summary} />
      <Changes summary={summary} />
      <Field
        label={t("Mot de passe de promotion")}
        hint={t("Celui défini depuis le Terminal du Mac. Cinq erreurs en une heure bloquent pendant une heure les promotions et le Terminal (retour manuel, déploiement direct).")}
      >
        <span className="relative block">
          <input
            type={revealed ? "text" : "password"}
            data-attention="promotion-password"
            className={clsx(inputClass, "pr-9")}
            value={password}
            onChange={(e) => onPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                e.preventDefault();
                onSubmit();
              }
            }}
            autoComplete="one-time-code"
            data-1p-ignore
            data-lpignore="true"
            autoCapitalize="none"
            autoCorrect="off"
            maxLength={PASSWORD_MAX}
            autoFocus
            spellCheck={false}
            disabled={disabled}
          />
          <IconButton
            size="sm"
            data-promotion-reveal
            className="absolute top-1/2 right-0.5 -translate-y-1/2"
            onClick={(e) => {
              e.preventDefault();
              onReveal();
            }}
            title={reveal}
            aria-label={reveal}
            aria-pressed={revealed}
            disabled={disabled}
          >
            <span key={revealed ? "shown" : "hidden"} className={clsx("flex", motion("fade"))}>
              {revealed ? <EyeOff size={14} aria-hidden /> : <Eye size={14} aria-hidden />}
            </span>
          </IconButton>
        </span>
      </Field>
    </div>
  );
}

/** Versions de la préproduction et de la production. */
function Versions({ summary }: { summary: PromotionSummary }) {
  return (
    <div className="rounded-lg border border-line px-3">
      <PropRow label={t("En préproduction")}>{t("Version du {date}", { date: formatBuild(summary.preprod.build) })}</PropRow>
      <PropRow label={t("En production")}>
        {summary.production.reachable && summary.production.build
          ? t("Version du {date}", { date: formatBuild(summary.production.build) })
          : t("Ne répond pas")}
      </PropRow>
      {summary.preprod.manifest_sha256 && (
        <PropRow label={t("Empreinte de la version")}>
          <span className="font-mono tabular-nums">{formatFingerprint(summary.preprod.manifest_sha256)}</span>
        </PropRow>
      )}
    </div>
  );
}

/** Numéro et nouveautés de la version promue, tels que les utilisateurs les verront au bas de la barre latérale :
 *  relus avant de promouvoir. */
export function ReleaseReview({ summary }: { summary: PromotionSummary }) {
  const release = summary.release;
  if (!release) return null;
  const lines = releaseLines(release, lang());
  return (
    <section aria-label={t("Nouveautés affichées aux utilisateurs")}>
      <SectionLabel as="h3" inset className="mb-1.5">
        {t("Nouveautés affichées aux utilisateurs")}
      </SectionLabel>
      <div className="rounded-lg border border-line px-4 py-2.5">
        <p className="mb-1.5 text-ui font-semibold text-ink">{t("Version {n}", { n: release.version })}</p>
        <ul className="space-y-1.5">
          {lines.map((line, i) => (
            <li key={i} className="flex gap-2 text-ui leading-snug text-ink">
              <span aria-hidden className="mt-[7px] size-1 shrink-0 rounded-full bg-ink-muted" />
              <span className="min-w-0">{line}</span>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-meta leading-snug text-ink-muted">
          {t("Visibles au survol du numéro de version, au bas de la barre latérale, dès la promotion.")}
        </p>
      </div>
    </section>
  );
}

/** « Ce qui va changer » : modifications (repliées), mises à jour de la base, coupure, exercices DIREX en cours. */
export function Changes({ summary }: { summary: PromotionSummary }) {
  const { changes, migrations } = summary;
  const updates = migrations.known ? migrations.items.length : 0;
  const direx = summary.production.direx_running ?? 0;
  return (
    <section aria-label={t("Ce qui va changer")}>
      <SectionLabel as="h3" inset className="mb-1.5">
        {t("Ce qui va changer")}
      </SectionLabel>
      <div className="rounded-lg border border-line">
        {changes.available && changes.count > 0 && lang() === "en" ? (
          // Titres des modifications rédigés en français (historique du dépôt) : en anglais, le nombre seul.
          <p className="flex min-h-8 items-center border-b border-line px-4 py-1.5 text-ui text-ink">
            {plural(changes.count, t("modification de la version"), t("modifications de la version"))}
          </p>
        ) : changes.available && changes.count > 0 ? (
          <Collapsible
            title={plural(changes.count, t("modification de la version"), t("modifications de la version"))}
            trailing={<span className="shrink-0 text-meta text-ink-muted">{t("Détail")}</span>}
          >
            <ul className="space-y-1.5">
              {changes.items.map((item, i) => (
                <li key={i} className="flex gap-3 text-ui leading-snug">
                  <span className="shrink-0 text-ink-muted tabular-nums">{formatParisDay(item.date)}</span>
                  <span className="min-w-0 break-words text-ink">{item.title}</span>
                </li>
              ))}
            </ul>
            {changes.count > changes.items.length && (
              <p className="mt-2 text-meta text-ink-muted">
                {t("Et {n} autres.", { n: changes.count - changes.items.length })}
              </p>
            )}
            {!changes.base_matches && (
              <p className="mt-2 text-meta leading-snug text-ink-muted">
                {t("Liste indicative : elle a été établie depuis une autre version que celle de la production.")}
              </p>
            )}
          </Collapsible>
        ) : (
          <p className="flex min-h-8 items-center border-b border-line px-4 py-1.5 text-ui text-ink-muted">
            {changes.available ? t("Aucune modification listée.") : t("Liste des modifications indisponible.")}
          </p>
        )}
        <div className="px-4">
          <PropRow label={t("Base de données")}>
            {!migrations.known
              ? t("Inconnue")
              : updates
                ? plural(updates, t("mise à jour"), t("mises à jour"))
                : t("Aucune mise à jour")}
          </PropRow>
          <PropRow label={t("Coupure estimée de la production")}>{formatDowntime(summary.estimated_downtime_s)}</PropRow>
        </div>
      </div>
      {direx > 0 && (
        <Callout tone="warn" className="mt-3">
          {t("{exercises} en cours en production : prévenez les participants de la coupure.", {
            exercises: plural(direx, t("exercice DIREX"), t("exercices DIREX")),
          })}
        </Callout>
      )}
      <p className="mt-3 text-meta leading-snug text-ink-muted">
        {t("Seule la version (code et site) passe en production ; les données de la préproduction n'y vont jamais.")}
      </p>
    </section>
  );
}

/** Suivi et résultat : étapes, temps écoulé, issue. */
export function ProgressBody({
  phase,
  status,
  summary,
  stalled,
  now,
}: {
  phase: ReturnType<typeof dialogPhase>;
  status: PromotionStatus;
  summary: PromotionSummary;
  stalled: boolean;
  now: number;
}) {
  const started = Date.parse(status.started_at);
  const ended = status.ended_at ? Date.parse(status.ended_at) : now;
  const elapsed = Number.isNaN(started) ? null : (ended - started) / 1000;
  const before = status.production_before ?? summary.production.build;
  return (
    <Motion origin="fade" className="space-y-4 px-4 py-4">
      <Presence show={phase === "running" && stalled}>
        {phase === "running" && stalled && (
          <Callout tone="warn">
            {t("Le service de promotion ne répond pas : la demande attend d'être prise en charge. La production n'a pas changé.")}
          </Callout>
        )}
      </Presence>
      {phase === "succeeded" && (
        <Callout tone="ok" title={t("Version du {date} en production", { date: formatBuild(status.production_after ?? status.preprod_build) })}>
          {status.downtime_s !== null && t("Coupure de {s} s.", { s: Math.round(status.downtime_s) })}{" "}
          {t("La sauvegarde de la production d'avant la promotion est gardée.")}
        </Callout>
      )}
      {phase === "failed_restored" && (
        <Callout tone="danger" title={t("Promotion échouée")}>
          {reasonText(status.reason, status)}{" "}
          {t("La production est revenue à la version du {date}. Au plus les saisies de la dernière minute sont perdues.", {
            date: formatBuild(before),
          })}
        </Callout>
      )}
      {phase === "failed_unrestored" && (
        <Callout tone="danger" title={t("Production à rétablir")}>
          {reasonText(status.reason, status)}{" "}
          {t("La production n'a pas pu être rétablie d'elle-même : intervention nécessaire depuis le Terminal du Mac (mode d'emploi d'exploitation, « Promotion interrompue »).")}
        </Callout>
      )}
      {phase === "interrupted_restored" && (
        <Callout tone="warn" title={t("Promotion interrompue")}>
          {t("La promotion a été interrompue (arrêt ou redémarrage du serveur) : la production est revenue à la version du {date}.", {
            date: formatBuild(before),
          })}
        </Callout>
      )}
      {phase === "aborted" && (
        <Callout tone="info" title={t("Promotion abandonnée")}>
          {status.reason && `${reasonText(status.reason, status)} `}
          {t("La production n'a pas changé.")}
        </Callout>
      )}
      <ol className="rounded-lg border border-line px-3" aria-label={t("Étapes de la promotion")}>
        {shownSteps(status).map((step) => (
          <li key={step.id} className="flex min-h-8 items-center gap-2.5 border-b border-line py-1.5 last:border-b-0">
            <StepIcon state={step.state} />
            <span
              className={clsx(
                "min-w-0 flex-1 text-ui",
                step.state === "todo" || step.state === "skipped" ? "text-ink-muted" : "text-ink",
                step.state === "running" && "font-medium",
              )}
            >
              {t(STEP_LABELS[step.id])}
            </span>
            {step.state === "skipped" && <span className="shrink-0 text-meta text-ink-muted">{t("Sans objet")}</span>}
            {step.state === "failed" && <span className="shrink-0 text-meta text-danger">{t("Échec")}</span>}
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-meta text-ink-muted">
        {elapsed !== null && <span className="tabular-nums">{t("Temps écoulé : {time}", { time: formatElapsed(elapsed) })}</span>}
        {phase === "running" && <span>{t("Vous pouvez fermer cette fenêtre : la promotion continue sur le serveur.")}</span>}
      </div>
    </Motion>
  );
}

function StepIcon({ state }: { state: PromotionStepState }) {
  if (state === "running") return <Spinner decorative tone="run" />;
  if (state === "done") return <Check size={14} aria-hidden className="shrink-0 text-ok" />;
  if (state === "failed") return <X size={14} aria-hidden className="shrink-0 text-danger" />;
  return <Circle size={14} aria-hidden className="shrink-0 text-line-strong" />;
}
